import { Box, Typography, Button, TextField, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Rating, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { Formik, Form, Field } from 'formik'
import { Link, useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'

import TryAgainMessageBlock from '../../components/common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../components/common/DataLoadingSpinner'
import { useUserStore } from '../../store/user.store'
import aliveLogo from '../../assets/alivelogo.png'
import api from '../../service/api'
import { observer } from 'mobx-react'
import decode from 'jwt-decode'
import { toast } from 'react-toastify'

const getUserId = () => {
    const meetingAccessToken = JSON.parse( sessionStorage.getItem( 'meetingAccessToken' ) || '{}' )
    const token = meetingAccessToken.token
    if ( !token )
        return null
    const decoded = decode( token )

    return decoded?.email ?? decoded?.user_id
}

const feedbackSchema = Yup.object().shape( {
    videoStable: Yup.string().required( 'Please select an option' ),
    audioStable: Yup.string().required( 'Please select an option' ),
    vitualboardStable: Yup.string().required( 'Please select an option' ),
    ableToCreatePoll: Yup.string().required( 'Please select an option' ),
    overallExperience: Yup.number()
        .min( 1, 'Please rate your experience' )
        .required( 'Please provide your rating' ),
    remark: Yup.string().max( 300, 'Maximum 300 characters allowed' ),
    user_email: Yup.string().required( "User ID used is required!" )
} )

const MeetingFeedback = observer( () => {

    const [submitting, setSubmitting] = useState( false )
    const [idRetrieved] = useState( Boolean( getUserId() ) )
    const [submitted, setSubmitted] = useState( false )
    const [meeting, setMeeting] = useState( null )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )

    const { palette, border, shadows } = useTheme()
    const { meeting_id } = useParams()
    const navigate = useNavigate()

    const UserStore = useUserStore()

    const initialValues = {
        videoStable: '',
        audioStable: '',
        vitualboardStable: '',
        ableToCreatePoll: '',
        overallExperience: 0,
        remark: '',
        user_email: getUserId()
    }

    const backToHome = () => {
        const userRole = UserStore.getUser?.user_role
        if ( userRole ) {
            navigate( ["FACULTY", "HOD", "FACULTY"].includes( userRole ) ? String( meeting.created_by ) === String( UserStore.getUser.user_id ) ? `/faculty/meeting/${meeting_id}` : `/faculty/meetings` : userRole === "STUDENT" ? `/student/home` : `/admin/audit` )
        }
        else {
            navigate( '/meetings' )
        }
    }

    const rejoin = () => {
        navigate( `/${meeting.meeting_type === 'webinar' ? "webinar" : meeting.meeting_type === 'class' ? "class" : "meeting"}/${meeting_id}` )
    }

    const handleSubmit = async ( values, { resetForm } ) => {
        setSubmitting( true )
        try {
            await api.onlineMeetings.submitFeedback( meeting_id, values )
            setSubmitted( true )
            resetForm()
            backToHome()
            toast( "Thank you! Your feedback is registered successfully." )
        } catch ( err ) {
            toast( err?.response?.data?.message || 'Failed to submit feedback:', err )
        } finally {
            setSubmitting( false )
        }
    }

    const fetchMeeting = useCallback( async () => {
        setLoading( true )
        try {
            UserStore.fetchUser()
            const { data: { data: meeting } } = await api.onlineMeetings.fetchMeeting( meeting_id )
            setMeeting( meeting )
            setError( null )
            return true
        } catch ( err ) {
            console.log( err )
            setError( { message: err?.response?.data?.message || "Failed to fetch details!", code: err?.response?.status } )
            return false
        } finally {
            setLoading( false )
        }
    }, [meeting_id, UserStore] )

    useEffect( () => {
        fetchMeeting()
    }, [fetchMeeting] )



    return (
        <Box bgcolor={palette.contentBg} flexGrow={1} display="flex" flexDirection="column" height="100%" overflow="auto" >
            {loading && !error && <DataLoadingSpinner waitingMessage="Loading details..." />}
            {error && <TryAgainMessageBlock err={error.message} code={error.code} getData={fetchMeeting} loading={loading} />}
            {!loading && !error && meeting && <Box boxShadow={shadows[1]} borderBottom={border[1]} bgcolor={palette.background.paper} alignItems="center" justifyContent=" space-between" height={`${50}px`} display="flex" maxWidth="100%" padding={{ md: "30px 120px", xs: "30px 10px" }} sx={{ transition: "0.2s ease-out" }}>
                <Link to={`${!UserStore?.getUser?.user_role ? "" : UserStore?.getUser?.user_role === "STUDENT" ? "" : "/faculty"}/meetings`} className='default-link' >
                    <Box display="flex" gap="10px" alignItems="center">
                        <Box component="img" width="30px" src={aliveLogo} alt="Alive Logo" />
                        <Typography variant='h6' fontWeight="300" fontSize={{ sm: "16px", xs: "14px" }}>
                            {`Alive ${meeting.meeting_type === 'webinar' ? "Webinar" : meeting.meeting_type === 'class' ? "Online Class" : "Online Meeting"}`}</Typography>
                    </Box>
                </Link>
                {<Box display="flex" alignItems="center" gap="10px">
                    <Box display={{ sm: "block", xs: "none" }}>
                        <Typography marginBottom="-5px" textAlign="right" variant='subtitle1' textTransform="capitalize">{new Date().toLocaleDateString( 'en-IN', { month: "long", year: "numeric", day: "2-digit" } )}</Typography>
                    </Box>
                </Box>}
            </Box>}
            {!loading && !error && meeting && (
                <Box borderRadius="20px" bgcolor={palette.background.paper} border={border[1]} boxShadow={shadows[1]} padding="20px" marginY="20px" marginX={{ md: "120px", xs: "10px" }} >
                    <Typography variant="h5" >
                        Rate your experiance for this {meeting.meeting_type === 'webinar' ? "Webinar" : meeting.meeting_type === 'class' ? "Class" : "Meeting"}
                    </Typography>
                    <Typography component="p" color='textSecondary' variant="subtitle3" >
                        Your feedback helps us improve future {meeting.meeting_type === 'webinar' ? "webinar" : meeting.meeting_type === 'class' ? "class" : "meeting"}.
                    </Typography>
                    <Box padding="20px" marginTop="20px" borderRadius="20px" bgcolor={palette.contentBg}>
                        <Typography variant='h6'>
                            Session Details
                        </Typography>
                        <Typography display="block" variant="body2">
                            Meeting Topic: {meeting?.title || ''}
                        </Typography>
                        <Typography display="block" variant="body2">
                            Host: {meeting?.creator_data?.name || ''}
                        </Typography>
                        <Typography display="block" variant="body2">
                            Type: {meeting.meeting_type === 'webinar' ? "Webinar" : meeting.meeting_type === 'class' ? "Class" : "Meeting"}
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap="10px" alignItems="center" marginTop="20px">
                            <Button onClick={rejoin} sx={{ background: `linear-gradient(125.39deg, #139CFF -32.54%, #C54097 110.4%)`, borderRadius: "20px" }} variant='contained' disableElevation  >Re-join {meeting.meeting_type === 'webinar' ? "Webinar" : meeting.meeting_type === 'class' ? "Class" : "Meeting"}</Button>
                            <Button onClick={backToHome} sx={{ borderRadius: "20px" }} variant='contained' color="error" disableElevation >{String( meeting.created_by ) === String( UserStore.getUser.user_id ) ? "View attendance" : "Back to home"}</Button>
                        </Box>
                    </Box>

                    {submitted ? (
                        <Typography color="success.main" mt={2}>
                            ✅ Thank you! Your feedback has been submitted.
                        </Typography>
                    ) : (
                        <Formik
                            initialValues={initialValues}
                            validationSchema={feedbackSchema}
                            onSubmit={handleSubmit}
                        >
                            {( { values, errors, touched, handleChange, setFieldValue } ) => (
                                <Form>

                                    <FormControl fullWidth margin="normal">
                                        <Typography gutterBottom display="block" variant='subtitle2' component="label"  >User E-mail</Typography>
                                        <TextField
                                            size='small'
                                            type='email'
                                            color="secondary"
                                            placeholder="User ID used for attending meeting."
                                            name="remark"
                                            value={values.user_email}
                                            onChange={handleChange}
                                            variant="outlined"
                                        />
                                        {touched.user_email && errors.user_email && (
                                            <Typography color="error" variant='subtitle2' >{errors.user_email}</Typography>
                                        )}
                                    </FormControl>


                                    {/* Video Stable */}
                                    <FormControl fullWidth margin="normal">
                                        <Typography variant='subtitle2' component="label"  >Did you experience stable video feeds?</Typography>
                                        <RadioGroup
                                            name="videoStable"
                                            value={values.videoStable}
                                            onChange={handleChange}
                                            row
                                        >
                                            <FormControlLabel value="Yes" control={<Radio color="secondary" />} label="Yes" />
                                            <FormControlLabel value="No" control={<Radio color="secondary" />} label="No" />
                                        </RadioGroup>
                                        {touched.videoStable && errors.videoStable && (
                                            <Typography color="error" variant='subtitle2' >{errors.videoStable}</Typography>
                                        )}
                                    </FormControl>

                                    {/* Audio Stable */}
                                    <FormControl fullWidth margin="normal">
                                        <Typography variant='subtitle2' component="label"  >Did you experience stable audio feeds?</Typography>
                                        <RadioGroup
                                            name="audioStable"
                                            value={values.audioStable}
                                            onChange={handleChange}
                                            row
                                        >
                                            <FormControlLabel value="Yes" control={<Radio color="secondary" />} label="Yes" />
                                            <FormControlLabel value="No" control={<Radio color="secondary" />} label="No" />
                                        </RadioGroup>
                                        {touched.audioStable && errors.audioStable && (
                                            <Typography color="error" variant='subtitle2' >{errors.audioStable}</Typography>
                                        )}
                                    </FormControl>

                                    {/* Audio Stable */}
                                    <FormControl fullWidth margin="normal">
                                        <Typography variant='subtitle2' component="label"  >Did you experience smooth virtual board?</Typography>
                                        <RadioGroup
                                            name="vitualboardStable"
                                            value={values.vitualboardStable}
                                            onChange={handleChange}
                                            row
                                        >
                                            <FormControlLabel value="Yes" control={<Radio color="secondary" />} label="Yes" />
                                            <FormControlLabel value="No" control={<Radio color="secondary" />} label="No" />
                                            <FormControlLabel value="Didn't use this feature" control={<Radio color="secondary" />} label="Didn't use this feature" />
                                        </RadioGroup>
                                        {touched.vitualboardStable && errors.vitualboardStable && (
                                            <Typography color="error" variant='subtitle2' >{errors.vitualboardStable}</Typography>
                                        )}
                                    </FormControl>

                                    {/* Able to Create Poll */}
                                    <FormControl fullWidth margin="normal">
                                        <Typography variant='subtitle2' component="label"  >Were you able to create polls and receive poll results?</Typography>
                                        <RadioGroup
                                            name="ableToCreatePoll"
                                            value={values.ableToCreatePoll}
                                            onChange={handleChange}
                                            row
                                        >
                                            <FormControlLabel value="Yes" control={<Radio color="secondary" />} label="Yes" />
                                            <FormControlLabel value="No" control={<Radio color="secondary" />} label="No" />
                                            <FormControlLabel value="Didn't use this feature" control={<Radio color="secondary" />} label="Didn't use this feature" />
                                        </RadioGroup>
                                        {touched.ableToCreatePoll && errors.ableToCreatePoll && (
                                            <Typography color="error" variant='subtitle2' >{errors.ableToCreatePoll}</Typography>
                                        )}
                                    </FormControl>

                                    {/* Overall Experience */}
                                    <FormControl fullWidth margin="normal">
                                        <Typography variant='subtitle2' component="label"  >Overall Experience</Typography>
                                        <Rating

                                            name="overallExperience"
                                            value={values.overallExperience}
                                            onChange={( e, value ) =>
                                                setFieldValue( 'overallExperience', value )
                                            }
                                        />
                                        {touched.overallExperience && errors.overallExperience && (
                                            <Typography color="error" variant='subtitle2' >{errors.overallExperience}</Typography>
                                        )}
                                    </FormControl>

                                    {/* Remark */}
                                    <FormControl fullWidth margin="normal">
                                        <TextField
                                            color="secondary"
                                            placeholder="Remark (Optional)"
                                            name="remark"
                                            value={values.remark}
                                            onChange={handleChange}
                                            multiline
                                            rows={3}
                                            variant="outlined"
                                        />
                                        {touched.remark && errors.remark && (
                                            <Typography color="error" variant='subtitle2' >{errors.remark}</Typography>
                                        )}
                                    </FormControl>

                                    <Box mt={2}>
                                        <Button
                                            disableElevation
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={submitting}
                                        >
                                            {submitting ? 'Submitting...' : 'Submit Feedback'}
                                        </Button>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    )}
                </Box>
            )}

        </Box>
    )
}
)
export default MeetingFeedback
